import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'shared/ui/button';
import { Card } from 'shared/ui/card';
import { TextInfo } from 'shared/ui/text-info';
import PdfPolicyOverview from 'shared/assets/documents/example_policy.pdf';
import Table from 'antd/es/table';
import { GlobalStateContext } from 'app/providers/GlobalStateProvider';
import { AlertCircleIcon } from 'shared/ui/icons';
import { columns, getTermsData } from './detail-policy-overview.columns';
import { TABS, DISEASE_EXCLUSIONS } from './detail-policy-overview.constant';
import useCollapseTerms from '../hooks/useCollapseTerms';
import { PatientInfoCard } from '../components/patient-info-card';
import { PdfViewerPanel } from '../components/pdf-viewer-panel';
import { CollapsibleSection } from '../components/collapsible-section';

const SourceBadge = ({ children }) => (
  <span className="flex gap-1 items-center text-xs text-blue-600 font-medium px-1.5 py-0.5 bg-blue-100 rounded-3xl">
    <AlertCircleIcon width="14" color="#2563eb" /> {children}
  </span>
);

const DetailPolicyOverviewPage = () => {
  const navigate = useNavigate();
  const { setSmallSide } = useContext(GlobalStateContext);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [tabOverview, setTabOverview] = useState('RINGKASAN');
  const { collapseTerm, toggleCollapseTerm } = useCollapseTerms();

  const handleOpenPdf = () => {
    setPdfOpen(true);
    setSmallSide(true);
  };

  const handleClosePdf = () => {
    setSmallSide(false);
    setPdfOpen(false);
  };

  const datas = getTermsData(handleOpenPdf);
  const colClass = pdfOpen ? 'w-full' : 'w-1/2';

  return (
    <div className={`h-screen grid ${pdfOpen ? 'grid-cols-2' : ''} gap-4`}>
      <div className="flex flex-wrap overflow-y-auto h-full">
        <div className="w-full sticky top-0 bg-white z-20 pt-6 px-6 pb-4 gap-y-2">
          <div className="w-full flex items-center gap-2 border-b border-slate-200 pb-6 pt-4">
            <div className="w-1/2 flex items-center gap-2">
              <span
                className="text-sm font-normal text-slate-500 cursor-pointer hover:text-slate-800"
                onClick={() => navigate('/insurance-portal')}
              >
                {pdfOpen ? '...' : 'Provider Eligibility Lookup'}
              </span>
              <span className="text-slate-500">/</span>
              <span className="text-sm font-semibold text-slate-900">
                PT9876
              </span>
            </div>
            <div className="w-1/2 flex gap-4 items-center justify-end">
              <Button className="hidden" label="Export PDF" />
            </div>
          </div>
        </div>
        <div className="w-1/2 flex overflow-auto flex-col flex-grow justify-start bg-[#fcfcfd] px-6 pb-4 gap-y-6">
          <PatientInfoCard />
          <Card className="w-full flex gap-4">
            <div className="w-full flex justify-between">
              <p className="text-lg font-bold">Dokumen Syarat dan Ketentuan</p>
            </div>
            <div className="w-full flex items-center bg-gradient-to-r from-green-50 via-green-100 to-green-200 border-green-200 rounded-lg p-4 border-2">
              <p className="text-xl font-semibold">
                Tidak Perlu Pre-Authorization
              </p>
            </div>
            <div
              className={`w-full grid ${pdfOpen ? 'grid-cols-2' : 'grid-cols-5'} gap-4 border-2 rounded-xl p-4 mt-4`}
            >
              <TextInfo title="Polis" desc="Takaful Keluarga" />
              <TextInfo title="Nomor Polis" desc="AB123456789" />
              <TextInfo title="Produk Plan" desc="Plan A" />
              <TextInfo title="Mulai Pertanggungan" desc="14 Mar 2023" />
              <TextInfo title="Akhir Pertanggungan" desc="13 Mar 2026" />
            </div>

            <div
              className={`w-full grid ${pdfOpen ? 'grid-cols-1' : 'grid-cols-2'} gap-4 mt-4`}
            >
              <div className="w-full flex flex-col border rounded-xl p-6 gap-y-2 overflow-auto">
                <p className="text-xs font-medium text-slate-500">
                  Batas Manfaat Rawat Inap (IP)
                </p>
                <p className="text-lg font-medium text-slate-900">
                  Rp 250.000.000{' '}
                  <span className="text-sm font-normal text-slate-500">
                    / tahun
                  </span>
                </p>
                <p className="text-sm text-slate-500">
                  Dipakai tahun ini:{' '}
                  <span className="text-xs font-medium text-slate-900">
                    Rp 170.000.000
                  </span>
                </p>
              </div>
              <div className="w-full flex flex-col border rounded-xl p-6 gap-y-2 overflow-auto">
                <p className="text-xs font-medium text-slate-500">
                  Batas Manfaat Rawat Jalan (OP)
                </p>
                <p className="text-lg font-medium text-slate-900">
                  Rp 100.000.000{' '}
                  <span className="text-sm font-normal text-slate-500">
                    / tahun
                  </span>
                </p>
                <p className="text-sm text-slate-500">
                  Dipakai tahun ini:{' '}
                  <span className="text-xs font-medium text-slate-900">
                    Rp 100.000.000 (Maks)
                  </span>
                </p>
              </div>
            </div>

            <div className="w-full flex flex-wrap border rounded-xl p-4">
              <div className="w-full flex space-x-4 my-5 border-b">
                {TABS.map((tab) => {
                  const isActive = tabOverview === tab.value;
                  return (
                    <div
                      key={tab.value}
                      onClick={() => setTabOverview(tab.value)}
                      className={`flex gap-2 cursor-pointer px-4 py-2 font-medium text-base justify-center items-center border-b-4 transition-all duration-200 rounded-t-lg ${
                        isActive
                          ? 'text-blue-500 border-blue-500'
                          : 'text-slate-500 border-transparent'
                      }`}
                    >
                      {isActive
                        ? React.cloneElement(tab.icon, { color: '#3b82f6' })
                        : tab.icon}
                      {tab.label}
                    </div>
                  );
                })}
              </div>

              <div
                className={`w-full mt-4 ${tabOverview === 'RINGKASAN' ? 'block' : 'hidden'}`}
              >
                <div className="w-full flex flex-wrap">
                  <div
                    className={`${colClass} flex flex-col mt-4 px-2 gap-y-4`}
                  >
                    <CollapsibleSection
                      title="Keaktifan Polis"
                      isOpen={collapseTerm.polis}
                      onToggle={() => toggleCollapseTerm('polis')}
                    >
                      <div className="flex flex-col gap-y-2">
                        <p className="text-sm">
                          1 Januari 2025 – 31 Desember 2025
                        </p>
                        <SourceBadge>
                          policy_agreement.pdf (Halaman 4)
                        </SourceBadge>
                      </div>
                    </CollapsibleSection>
                    <CollapsibleSection
                      title="Masa Tunggu"
                      isOpen={collapseTerm.waiting}
                      onToggle={() => toggleCollapseTerm('waiting')}
                    >
                      <div className="flex flex-col gap-y-2">
                        <div className="flex flex-col gap-2">
                          <span className="text-sm">
                            30 hari (penyakit umum)
                          </span>
                          <span className="text-sm">
                            60 hari (kondisi khusus)
                          </span>
                          <span className="italic text-sm">
                            *Tidak termasuk kecelakaan
                          </span>
                        </div>
                        <SourceBadge>
                          policy_agreement.pdf (Halaman 19)
                        </SourceBadge>
                      </div>
                    </CollapsibleSection>
                    <CollapsibleSection
                      title="Pengecualian Penyakit"
                      isOpen={collapseTerm.disease}
                      onToggle={() => toggleCollapseTerm('disease')}
                    >
                      <div className="flex flex-col gap-2">
                        <p className="text-sm">Polis tidak menanggung:</p>
                        <div className="w-full grid gap-2 pl-3">
                          {DISEASE_EXCLUSIONS.map((text, idx) => (
                            <p key={idx} className="text-sm">
                              • {text}
                            </p>
                          ))}
                        </div>
                        <SourceBadge>
                          policy_agreement.pdf (Halaman 19)
                        </SourceBadge>
                      </div>
                    </CollapsibleSection>
                  </div>
                  <div
                    className={`${colClass} flex flex-col mt-4 px-2 gap-y-4`}
                  >
                    <CollapsibleSection
                      title="Masa Kontestasi"
                      isOpen={collapseTerm.kontestasi}
                      onToggle={() => toggleCollapseTerm('kontestasi')}
                    >
                      <div className="flex flex-col gap-y-2">
                        <p className="text-sm">2 Tahun (sejak polis aktif)</p>
                        <SourceBadge>
                          policy_agreement.pdf (Halaman 14)
                        </SourceBadge>
                      </div>
                    </CollapsibleSection>
                    <CollapsibleSection
                      title="Wilayah Klaim"
                      isOpen={collapseTerm.claim}
                      onToggle={() => toggleCollapseTerm('claim')}
                    >
                      <div className="flex flex-col gap-y-2">
                        <p className="text-sm">
                          Asia - Kecuali Singapura, Jepang, dan Hong Kong.
                        </p>
                        <SourceBadge>
                          policy_agreement.pdf (Halaman 16)
                        </SourceBadge>
                      </div>
                    </CollapsibleSection>
                  </div>
                </div>
              </div>
              <div
                className={`w-full px-2 mt-4 ${tabOverview === 'DETAIL' ? 'block' : 'hidden'}`}
              >
                <Table
                  dataSource={datas}
                  columns={columns}
                  pagination={false}
                  components={{
                    header: {
                      cell: (props) => (
                        <th
                          {...props}
                          style={{ backgroundColor: '#f1f5f9', ...props.style }}
                        >
                          {props.children}
                        </th>
                      ),
                    },
                  }}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
      <PdfViewerPanel
        pdfOpen={pdfOpen}
        onClose={handleClosePdf}
        fileUrl={PdfPolicyOverview}
      />
    </div>
  );
};

export default DetailPolicyOverviewPage;
